import React, { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import SendIcon from '@/comp/common/icons/SendIcon';
import { useAutoResizeTextarea } from '@/hooks/useAutoResizeTextarea';
import * as S from './ChatInput.styles';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  isStreaming?: boolean;
  disabled?: boolean;
  isEmpty?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = observer(
  ({ onSend, isLoading = false, isStreaming = false, disabled = false, isEmpty = false }) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const previousStreamingRef = useRef<boolean>(isStreaming);

    const { resetHeight } = useAutoResizeTextarea(textareaRef, message, {
      isEmpty,
      minHeight: 40,
      maxLines: 9,
      emptyLineHeight: 20,
      filledLineHeight: 24,
    });

    // Focus textarea when AI response completes (isStreaming transitions from true to false)
    useEffect(() => {
      // Detect when streaming completes: was streaming, now not streaming, and not loading
      if (previousStreamingRef.current && !isStreaming && !isLoading && !disabled && textareaRef.current) {
        requestAnimationFrame(() => {
          textareaRef.current?.focus();
        });
      }
      previousStreamingRef.current = isStreaming;
    }, [isStreaming, isLoading, disabled]);

    const handleSend = () => {
      if (message.trim() && !isLoading && !disabled) {
        const messageToSend = message.trim();
        onSend(messageToSend);
        setMessage('');
        resetHeight();
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
    };

    return (
      <S.InputContainer $isEmpty={isEmpty}>
        <S.InputWrapper>
          <S.TextAreaWrapper $isEmpty={isEmpty}>
            <S.TextArea
              ref={textareaRef}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={t('AskAnything')}
              disabled={disabled || isLoading}
              rows={1}
              $isEmpty={isEmpty}
            />
            <S.SendButton onClick={handleSend} disabled={!message.trim() || isLoading || disabled} $isEmpty={isEmpty}>
              <SendIcon />
            </S.SendButton>
          </S.TextAreaWrapper>
        </S.InputWrapper>
      </S.InputContainer>
    );
  },
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;
